import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { Link } from "@inertiajs/react"
import { cn } from "@/lib/utils"

// Matches the rename-proof model-link tokens QuickPrice embeds in an overview:
// [[nameplate:<id>|<display name>]]. The id is resolved to a URL via the `links`
// map (keyed by id); the display name is what the reader sees.
const TOKEN_PATTERN = /\[\[nameplate:(\d+)\|([^\]]*)\]\]/g

// Lines shown before the clamp bites. Written as a literal so Tailwind's
// content scanner keeps the utility in the build.
const CLAMP_CLASS = "line-clamp-3"

// useLayoutEffect warns when it runs during SSR (there is nothing to measure on
// the server), so fall back to useEffect there. On the client the layout effect
// runs before paint, so correcting the "read more" button never flashes.
const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect

// Split the overview into an ordered list of nodes: plain-text runs and link
// tokens.
function parseOverview(text) {
    const nodes = []
    let lastIndex = 0
    for (const match of text.matchAll(TOKEN_PATTERN)) {
        if (match.index > lastIndex) {
            nodes.push({
                type: "text",
                value: text.slice(lastIndex, match.index),
            })
        }
        nodes.push({ type: "link", id: match[1], name: match[2] })
        lastIndex = match.index + match[0].length
    }
    if (lastIndex < text.length) {
        nodes.push({ type: "text", value: text.slice(lastIndex) })
    }
    return nodes
}

/**
 * Reader-visible character count — a link token contributes only its display
 * name, not its markup. Used purely as the server-side guess at whether the
 * clamp will bite; the client corrects it by measuring.
 */
function visibleLength(nodes) {
    return nodes.reduce(
        (total, node) =>
            total +
            (node.type === "text" ? node.value.length : node.name.length),
        0
    )
}

function renderNodes(nodes, links) {
    return nodes.map((node, index) => {
        if (node.type === "text") {
            return node.value
        }
        const href = links?.[node.id]
        if (!href) {
            // Unresolvable id (e.g. nameplate merged away): plain text fallback.
            return node.name
        }
        return (
            <Link
                key={index}
                href={href}
                className="font-medium text-[#8B0D2F] hover:underline"
            >
                {node.name}
            </Link>
        )
    })
}

/**
 * Renders an editorial overview in full — every character and every resolved
 * link token is always in the DOM, so crawlers (and `curl`) see the whole intro.
 * "Read more" is purely a CSS clamp over that text; expanding never fetches and
 * never re-renders different content.
 *
 * `clampAfter` is only the server-side heuristic for whether to render the
 * toggle at all. Once mounted, the toggle is driven by measuring the clamped
 * box, so it appears exactly when text is actually hidden at the current width.
 */
export default function ExpandableOverview({
    text,
    links,
    className,
    clampAfter = 300,
}) {
    const nodes = text ? parseOverview(text) : []
    const [expanded, setExpanded] = useState(false)
    const [isClamped, setIsClamped] = useState(
        () => visibleLength(nodes) > clampAfter
    )
    const paragraphRef = useRef(null)

    useIsomorphicLayoutEffect(() => {
        const paragraph = paragraphRef.current
        if (!paragraph || expanded) {
            return
        }

        // The clamp is applied whenever the overview is collapsed, so an
        // overflowing scroll height means text is genuinely hidden. Re-measured
        // on resize because wrapping — and therefore the line count — changes
        // with the viewport.
        const measure = () =>
            setIsClamped(paragraph.scrollHeight > paragraph.clientHeight + 1)

        measure()

        if (typeof ResizeObserver === "undefined") {
            return
        }
        const observer = new ResizeObserver(measure)
        observer.observe(paragraph)

        return () => observer.disconnect()
    }, [text, expanded])

    if (!text) {
        return null
    }

    return (
        <div className={cn("flex flex-col items-start gap-1", className)}>
            <p
                ref={paragraphRef}
                className={cn(
                    "text-base leading-relaxed text-[#5F5E5E]",
                    !expanded && CLAMP_CLASS
                )}
            >
                {renderNodes(nodes, links)}
            </p>
            {isClamped && (
                <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className="font-semibold text-[#8B0D2F] hover:underline"
                >
                    {expanded ? "show less" : "read more"}
                </button>
            )}
        </div>
    )
}
